import { useState, useMemo, useEffect } from 'react'
import { LAUNCH_CHARACTERS } from '../data/characters'
import { ELEMENTS } from '../types'
import type { Character, Element } from '../types'
import CharacterDetail from '../components/ui/CharacterDetail'
import { CharacterAPI } from '../api/client'

const FACTION_LABELS: Record<string, string> = {
  'drifters':      'The Drifters',
  'iron-meridian': 'Iron Meridian',
  'tidecallers':   'Tidecallers',
  'wrecked':       'The Wrecked',
  'sovereign-few': 'Sovereign Few',
}

const AWAKENING_CONDITIONS: Record<number, string[]> = {
  2: ['Complete 10 bounties with this character', 'Bond with a creature of same element'],
  3: ['Win 25 encounters at Stage 2', 'Reach Bond Level 80 with paired creature'],
}

type SortKey = 'default' | 'awakening' | 'attack' | 'speed'

// Map templateId → static Character for non-API fields (class, quote, zone, etc.)
const TEMPLATE_MAP: Record<string, Character> = Object.fromEntries(
  LAUNCH_CHARACTERS.map(c => [c.id, c])
)

export default function CharacterRoster() {
  const [selected, setSelected] = useState<Character | null>(null)
  const [elementFilter, setElementFilter] = useState<Element | 'all'>('all')
  const [sortKey, setSortKey] = useState<SortKey>('default')
  const [characters, setCharacters] = useState<Character[]>([])
  const [loading, setLoading] = useState(true)
  const [apiError, setApiError] = useState<string | null>(null)

  useEffect(() => {
    setLoading(true)
    CharacterAPI.getMyCharacters().then(res => {
      if (res.success && res.data && res.data.length > 0) {
        // Merge API owned-character data with static template fields
        const merged = (res.data as unknown as Array<{
          id: string
          templateId: string
          awakeningStage: number
          battleCount: number
          bondedCreatureId?: string | null
        }>).map(apiChar => {
          const template = TEMPLATE_MAP[apiChar.templateId]
          if (!template) return null
          return {
            ...template,
            id: apiChar.id,
            awakeningStage: apiChar.awakeningStage as 1 | 2 | 3,
            bondedCreatureId: apiChar.bondedCreatureId ?? template.bondedCreatureId,
            _templateId: apiChar.templateId,
          } as Character & { _templateId: string }
        }).filter(Boolean) as Character[]
        setCharacters(merged)
      } else if (!res.success) {
        setApiError(res.error ?? 'Could not load characters')
      }
    }).finally(() => setLoading(false))
  }, [])

  const filtered = useMemo(() => {
    let list = [...characters]
    if (elementFilter !== 'all') list = list.filter(c => c.element === elementFilter)
    if (sortKey === 'awakening') list.sort((a, b) => b.awakeningStage - a.awakeningStage)
    else if (sortKey === 'attack') list.sort((a, b) => b.stats.attack - a.stats.attack)
    else if (sortKey === 'speed') list.sort((a, b) => b.stats.speed - a.stats.speed)
    return list
  }, [characters, elementFilter, sortKey])

  const elementOptions: { label: string; value: Element | 'all' }[] = [
    { label: 'All', value: 'all' },
    ...(['fire','water','earth','air','void'] as Element[]).map(e => ({
      label: `${ELEMENTS[e].emoji} ${ELEMENTS[e].label}`,
      value: e,
    })),
  ]

  return (
    <div className="flex-1 flex flex-col" style={{ minHeight: '100vh', background: '#020510' }}>

      {/* Top Bar - Compact */}
      <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: 'rgba(58,58,90,0.5)' }}>
        <div className="flex items-center gap-3">
          <h1 className="font-bold text-lg uppercase tracking-wider" style={{ color: '#EAF6FF', fontFamily: 'Rajdhani, system-ui' }}>Your Crew</h1>
          {loading && <span className="text-[10px] text-[#8A8AA5] animate-pulse" style={{ fontFamily: 'Space Mono, monospace' }}>Syncing…</span>}
          {apiError && <span className="text-[10px] text-red-400" style={{ fontFamily: 'Space Mono, monospace' }}>⚠ offline</span>}
        </div>
        <div className="flex items-center gap-2">
          <span className="font-bold text-[10px] text-[#8A8AA5]" style={{ fontFamily: 'Space Mono, monospace' }}>
            {filtered.length} / {characters.length}
          </span>
        </div>
      </div>

      {/* Element Filter - Horizontal scroll */}
      <div className="flex gap-1.5 overflow-x-auto no-scrollbar px-4 py-3 border-b" style={{ borderColor: 'rgba(58,58,90,0.3)' }}>
        {elementOptions.map(opt => (
          <button
            key={opt.value}
            onClick={() => setElementFilter(opt.value)}
            className={`font-bold text-xs px-3 py-1.5 rounded-full border transition-all bg-transparent cursor-pointer whitespace-nowrap ${
              elementFilter === opt.value
                ? 'border-[#FFE800] border-opacity-60 text-[#FFE800] bg-[#FFE800] bg-opacity-5'
                : 'border-white border-opacity-10 text-[#8A8AA5] hover:border-opacity-20 hover:text-[#EAF6FF]'
            }`}
            style={{ fontFamily: 'Space Mono, monospace' }}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Sort - Horizontal scroll */}
      <div className="flex gap-2 overflow-x-auto no-scrollbar px-4 py-2">
        {[
          { key: 'default', label: 'Default' },
          { key: 'awakening', label: 'Awakening' },
          { key: 'attack', label: 'Attack' },
          { key: 'speed', label: 'Speed' },
        ].map(opt => (
          <button
            key={opt.key}
            onClick={() => setSortKey(opt.key as SortKey)}
            className={`font-bold text-[10px] px-3 py-1 rounded-full border transition-all bg-transparent cursor-pointer whitespace-nowrap ${
              sortKey === opt.key
                ? 'border-[#00F0FF] border-opacity-60 text-[#00F0FF] bg-[#00F0FF] bg-opacity-5'
                : 'border-white border-opacity-10 text-[#8A8AA5] hover:border-opacity-20'
            }`}
            style={{ fontFamily: 'Space Mono, monospace' }}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Content - Scrollable */}
      <div className="flex-1 overflow-y-auto p-4">
        {filtered.length === 0 ? (
          <div className="rounded-xl border p-8 text-center" style={{ background: 'rgba(13,13,21,0.95)', borderColor: 'rgba(58,58,90,0.5)' }}>
            <p className="text-3xl mb-3">👤</p>
            <p className="font-bold text-[#EAF6FF] mb-2" style={{ fontFamily: 'Rajdhani, system-ui' }}>No characters match</p>
            <button onClick={() => setElementFilter('all')} className="px-4 py-2 rounded-lg font-bold uppercase tracking-wider text-sm transition-all hover:opacity-80"
                    style={{ background: 'rgba(58,58,90,0.3)', color: '#8A8AA5', fontFamily: 'Space Mono, monospace' }}>
              Clear Filter
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(char => (
              <CharacterCard key={char.id} character={char} onSelect={setSelected} />
            ))}
          </div>
        )}
      </div>

      <CharacterDetail character={selected} onClose={() => setSelected(null)} />
    </div>
  )
}


const SPRITE = (id: string, pose: string) => `/assets/characters/${id}/${pose}.png`

const CHAR_PORTRAITS: Record<string, string> = {
  'kai-voss':          SPRITE('kai-voss', 'front'),
  'sela-mourne':       SPRITE('sela-mourne', 'front'),
  'drego':             SPRITE('drego', 'front'),
  'vara-six':          SPRITE('vara-six', 'front'),
  'marshal-coen':      SPRITE('marshal-coen', 'front'),
  'lune':              SPRITE('lune', 'front'),
  'reef':              SPRITE('reef', 'front'),
  'oska-unmade':       SPRITE('oska-unmade', 'front'),
  'twin-daggers-yara': SPRITE('twin-daggers-yara', 'front'),
}

const ELEMENT_ICONS: Record<string, string> = {
  fire: '/assets/icons/element-fire.png',
  water: '/assets/icons/element-water.png',
  earth: '/assets/icons/element-earth.png',
  air: '/assets/icons/element-air.png',
  void: '/assets/icons/element-void.png',
  aether: '/assets/icons/element-aether.png',
}

function CharacterCard({ character, onSelect }: { character: Character; onSelect: (c: Character) => void }) {
  const el = ELEMENTS[character.element]
  const canAdvance = character.awakeningStage < 3
  const nextStageConditions = AWAKENING_CONDITIONS[character.awakeningStage + 1] ?? []
  const templateId = (character as Character & { _templateId?: string })._templateId ?? character.id
  const portrait = CHAR_PORTRAITS[templateId]

  return (
    <div
      onClick={() => onSelect(character)}
      className="group relative overflow-hidden rounded-xl border cursor-pointer transition-all duration-240"
      style={{
        background: 'linear-gradient(160deg, rgba(13,13,21,0.95), rgba(8,8,14,0.98))',
        borderColor: 'rgba(58,58,90,0.5)',
        boxShadow: '0 0 0 1px rgba(0,0,0,0.5)',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = `0 0 16px ${el.color}40, 0 0 0 1px ${el.color}30`;
        e.currentTarget.style.borderColor = `${el.color}50`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = '0 0 0 1px rgba(0,0,0,0.5)';
        e.currentTarget.style.borderColor = 'rgba(58,58,90,0.5)';
      }}
    >
      {/* Glow layer */}
      <div 
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-240 pointer-events-none"
        style={{ background: `radial-gradient(circle at 50% 0%, ${el.color}08, transparent 70%)` }}
      />
      
      <div className="relative z-10 p-5">
        {/* Header: sprite + name/element */}
        <div className="flex items-start gap-4 mb-4">
          <div className="flex-shrink-0 w-24 h-36 rounded-xl overflow-hidden bg-black/30 relative">
            <img
              src={portrait}
              alt={character.name}
              className="w-full h-full object-contain object-center character-float"
              style={{
                filter: `drop-shadow(0 0 16px ${el.color}50)`,
                imageRendering: 'crisp-edges',
              }}
              onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
            />
          </div>
          <div className="flex-1 min-w-0 pt-2">
            <h3 className="font-bold text-xl text-[#EAF6FF] leading-tight tracking-tight uppercase" style={{ fontFamily: 'Rajdhani, system-ui, sans-serif' }}>
              {character.name}
            </h3>
            <p className="text-xs text-[#8A8AA5] mt-1 tracking-wide" style={{ fontFamily: 'Space Mono, monospace' }}>
              {character.title}
            </p>
            {/* Element badge with icon */}
            <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded border"
              style={{ 
                borderColor: `${el.color}40`,
                background: `${el.color}10`,
              }}
            >
              <img src={ELEMENT_ICONS[character.element]} alt={el.label} className="w-4 h-4" />
              <span className="text-xs font-bold uppercase tracking-wider" style={{ color: el.color, fontFamily: 'Space Mono, monospace' }}>
                {el.label}
              </span>
            </div>
            <p className="text-[10px] text-[#5A5A7A] mt-1 uppercase tracking-wider" style={{ fontFamily: 'Space Mono, monospace' }}>
              {FACTION_LABELS[character.faction] ?? character.faction}
            </p>
          </div>
        </div>

      {/* Quote */}
      <p className="text-body text-xs italic mb-4 line-clamp-2 opacity-60 leading-relaxed">
        "{character.quote}"
      </p>

      {/* Stats bars */}
      <div className="grid grid-cols-4 gap-2 mb-4">
        {[
          { label: 'ATK', value: character.stats.attack },
          { label: 'DEF', value: character.stats.defense },
          { label: 'SPD', value: character.stats.speed },
          { label: 'SPC', value: character.stats.special },
        ].map(stat => (
          <div key={stat.label} className="text-center">
            <p className="text-label text-[10px] mb-0.5">{stat.label}</p>
            <p className="font-display font-black text-xs mb-1" style={{ color: el.color }}>{stat.value}</p>
            <div className="stat-bar">
              <div
                className="stat-bar-fill"
                style={{
                  width: `${stat.value}%`,
                  background: `linear-gradient(90deg, ${el.color}60, ${el.color})`,
                }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Moves list */}
      <div className="space-y-1 mb-4">
        {character.moves.map(move => (
          <div
            key={move.name}
            className="flex items-center justify-between px-3 py-1.5 rounded"
            style={{ background: 'rgba(255,255,255,0.03)', borderLeft: `2px solid ${el.color}50` }}
          >
            <span className="font-display font-bold text-xs text-ab-text">{move.name}</span>
            <div className="flex items-center gap-2">
              <span className="text-label text-[10px] capitalize">{move.type}</span>
              <span className="font-display font-black text-xs" style={{ color: el.color }}>{move.power}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Awakening track */}
      <div className="pt-3 border-t border-white border-opacity-5">
        <div className="flex justify-between items-center mb-2">
          <p className="text-label text-xs">Awakening</p>
          <p className="font-display font-bold text-xs" style={{ color: el.color }}>
            Stage {character.awakeningStage} / 3
          </p>
        </div>
        <div className="flex gap-1.5 mb-2">
          {[1, 2, 3].map(stage => (
            <div
              key={stage}
              className={stage <= character.awakeningStage ? 'awakening-node-active' : 'awakening-node'}
              style={stage <= character.awakeningStage ? {
                background: `linear-gradient(90deg, ${el.color}, ${el.color}80)`,
                boxShadow: `0 0 8px ${el.color}50`,
              } : undefined}
            />
          ))}
        </div>
        {/* Next stage conditions */}
        {canAdvance && nextStageConditions.length > 0 && (
          <div className="space-y-0.5 mt-1.5">
            {nextStageConditions.map(cond => (
              <p key={cond} className="text-[10px] font-display font-bold text-ab-text-dim opacity-50 flex items-center gap-1.5">
                <span className="w-1 h-1 rounded-full bg-current flex-shrink-0 inline-block" />
                {cond}
              </p>
            ))}
          </div>
        )}
      </div>
      </div>

      {/* Bond indicator */}
      {character.bondedCreatureId && (
        <div className="flex items-center justify-between mt-3 pt-2 border-t border-white border-opacity-5">
          <span className="text-label text-xs">🐾 Bonded — {character.bondedCreatureId.replace(/-/g, ' ')}</span>
        </div>
      )}
    </div>
  )
}
